import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  LinearProgress,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  CircularProgress,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CustomAxios from '../utils/CustomAxios';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

const COM_FILTER_LABELS = {
  communication_mode: 'Channel',
  mid: 'Member ID range',
  sh: 'Share balance range',
  sb: 'Subscription balance range',
  age: 'Age range',
  sex: 'Sex',
  race: 'Race',
  po: 'Photo',
  state: 'State',
  estate: 'Estate',
  region: 'Region',
  postal_code: 'Postal code',
  nomailer: 'No mailer',
  deceased: 'Deceased',
  dispAction: 'Disciplinary action',
  director: 'Director',
  observer: 'Observer',
  delegate: 'Delegate',
  company: 'Company',
  junior: 'Junior',
  terminated: 'Terminated',
};

function formatComFilterValue(key, value) {
  if (value === null || value === undefined || value === '') return null;
  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    if (value.length === 2) return `${value[0]} - ${value[1]}`;
    return value.join(', ');
  }
  if (key === 'communication_mode' && typeof value === 'string') {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }
  return String(value);
}

export function CommunicationWhatsApp() {
  const navigate = useNavigate();
  const formPayload = useSelector((state) => state.executive.formPayload);

  const comFilters = useMemo(() => {
    return formPayload?.COM || formPayload?.['COM'] || {};
  }, [formPayload]);

  const recipientFilterRows = useMemo(() => {
    const skipKeys = new Set(['content']);
    return Object.entries(comFilters)
      .filter(([k]) => !skipKeys.has(k))
      .map(([k, v]) => {
        const display = formatComFilterValue(k, v);
        if (display === null) return null;
        return {
          id: k,
          label: COM_FILTER_LABELS[k] || k.replace(/_/g, ' '),
          value: display,
        };
      })
      .filter(Boolean);
  }, [comFilters]);

  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templateError, setTemplateError] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [sending, setSending] = useState(false);
  const [sendConfirmOpen, setSendConfirmOpen] = useState(false);
  const [recipientPreviewCount, setRecipientPreviewCount] = useState(null);
  const [previewCountLoading, setPreviewCountLoading] = useState(false);
  const [previewCountError, setPreviewCountError] = useState('');
  const [campaignHistory, setCampaignHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [campaignId, setCampaignId] = useState(null);
  const [progress, setProgress] = useState(null);
  const [mediaSource, setMediaSource] = useState('device');
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaUrl, setMediaUrl] = useState('');
  const [mediaUploading, setMediaUploading] = useState(false);
  const [cloudMedia, setCloudMedia] = useState([]);
  const [loadingCloudMedia, setLoadingCloudMedia] = useState(false);

  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const formatFilters = (item) => {
    const recipients = item?.filters?.recipients;
    if (recipients && typeof recipients === 'object' && !Array.isArray(recipients)) {
      return Object.entries(recipients)
        .filter(([, value]) => value !== null && value !== undefined && String(value).trim() !== '')
        .map(([key, value]) => `${key}: ${value}`)
        .join(', ');
    }
    return '-';
  };

  const selectedTemplate = useMemo(
    () => templates.find((t) => String(t.id) === String(selectedTemplateId)) || null,
    [templates, selectedTemplateId]
  );
  const previewHeaderImage = useMemo(() => {
    if (!selectedTemplate || !Array.isArray(selectedTemplate.components)) return '';
    const header = selectedTemplate.components.find(
      (c) => String(c?.type || '').toUpperCase() === 'HEADER' && String(c?.format || '').toUpperCase() === 'IMAGE'
    );
    return header?.example?.header_handle?.[0] || '';
  }, [selectedTemplate]);
  const previewHeaderText = useMemo(() => {
    if (!selectedTemplate || !Array.isArray(selectedTemplate.components)) return '';
    const header = selectedTemplate.components.find((c) => String(c?.type || '').toUpperCase() === 'HEADER');
    if (!header) return '';
    if (typeof header.text === 'string' && header.text.trim()) return header.text;
    if (String(header.format || '').toUpperCase() !== 'IMAGE') {
      return `${header.format || 'HEADER'} header`;
    }
    return '';
  }, [selectedTemplate]);
  const previewBodyText = useMemo(() => {
    if (!selectedTemplate) return 'No template body available.';
    if (!Array.isArray(selectedTemplate.components)) return selectedTemplate.body || 'No template body available.';
    return (
      selectedTemplate.components.find((c) => String(c?.type || '').toUpperCase() === 'BODY')?.text ||
      selectedTemplate.body ||
      'No template body available.'
    );
  }, [selectedTemplate]);
  const selectedHeaderFormat = useMemo(() => {
    if (!selectedTemplate || !Array.isArray(selectedTemplate.components)) return '';
    const header = selectedTemplate.components.find((c) => String(c?.type || '').toUpperCase() === 'HEADER');
    if (!header) return '';
    const format = String(header.format || '').toUpperCase();
    return ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(format) ? format : '';
  }, [selectedTemplate]);
  const mediaAccept = useMemo(() => {
    if (selectedHeaderFormat === 'IMAGE') return 'image/*';
    if (selectedHeaderFormat === 'VIDEO') return 'video/*';
    if (selectedHeaderFormat === 'DOCUMENT') return '.pdf,.doc,.docx,.txt,.xlsx,.xls,.ppt,.pptx';
    return '*/*';
  }, [selectedHeaderFormat]);
  const isMediaRequired = Boolean(selectedHeaderFormat);
  const canSendCampaign = Boolean(
    !sending &&
      selectedTemplate &&
      recipientFilterRows.length > 0 &&
      (!isMediaRequired || Boolean(mediaUrl))
  );

  const fetchTemplates = useCallback(async () => {
    try {
      setLoadingTemplates(true);
      setTemplateError('');
      const res = await CustomAxios.get('oasis/whatsapp/templates/');
      const list = res?.data?.data || [];
      setTemplates(list);
      if (!selectedTemplateId && list.length > 0) {
        setSelectedTemplateId(String(list[0].id));
      }
    } catch (e) {
      setTemplateError(e?.response?.data?.error || e.message || 'Failed to load templates.');
    } finally {
      setLoadingTemplates(false);
    }
  }, [selectedTemplateId]);

  useEffect(() => {
    if (!comFilters || Object.keys(comFilters).length === 0) {
      setToast({ open: true, message: 'Please apply WhatsApp filters first.', severity: 'error' });
      return;
    }
    const mode = String(comFilters.communication_mode || '').toLowerCase();
    if (mode !== 'whatsapp') {
      setToast({ open: true, message: 'Selected communication mode is not WhatsApp.', severity: 'error' });
    }
  }, [comFilters]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  useEffect(() => {
    setMediaSource('device');
    setMediaFile(null);
    setMediaUrl('');
    setCloudMedia([]);
  }, [selectedTemplateId]);

  useEffect(() => {
    if (!selectedHeaderFormat || mediaSource !== 'cloud') return;
    let active = true;
    const fetchCloudMedia = async () => {
      setLoadingCloudMedia(true);
      try {
        const res = await CustomAxios.get(
          `oasis/whatsapp/campaign-media/?media_type=${selectedHeaderFormat.toLowerCase()}`
        );
        if (!active) return;
        setCloudMedia(res?.data?.data || []);
      } catch (e) {
        if (!active) return;
        setCloudMedia([]);
        const msg = e?.response?.data?.error || e.message || 'Failed to load cloud media.';
        setToast({ open: true, message: msg, severity: 'error' });
      } finally {
        if (active) {
          setLoadingCloudMedia(false);
        }
      }
    };
    fetchCloudMedia();
    return () => {
      active = false;
    };
  }, [mediaSource, selectedHeaderFormat]);

  useEffect(() => {
    if (!campaignId) return;

    const intervalId = setInterval(async () => {
      try {
        const res = await CustomAxios.get(`oasis/campaign/status/?campaign_id=${campaignId}`);
        const data = res?.data;
        if (data) {
          setProgress(data);
          if (data.status && data.status !== 'processing') {
            clearInterval(intervalId);
          }
        }
      } catch (e) {
        console.error('WhatsApp status polling failed:', e);
      }
    }, 3000);

    return () => clearInterval(intervalId);
  }, [campaignId]);

  const fetchCampaignHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await CustomAxios.get('oasis/campaign/history/?communication_mode=whatsapp&limit=10');
      setCampaignHistory(res?.data?.results || []);
    } catch (e) {
      setCampaignHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    fetchCampaignHistory();
  }, []);

  const buildWhatsAppPayload = () => {
    const recipients = recipientFilterRows.reduce((acc, row) => {
      acc[row.id] = row.value;
      return acc;
    }, {});

    const templateToSend = JSON.parse(JSON.stringify(selectedTemplate || {}));
    if (selectedHeaderFormat && mediaUrl && Array.isArray(templateToSend.components)) {
      const header = templateToSend.components.find((c) => String(c?.type || '').toUpperCase() === 'HEADER');
      if (header) {
        header.example = header.example || {};
        header.example.header_handle = [mediaUrl];
      }
    }

    return {
      ...comFilters,
      communication_mode: 'whatsapp',
      subject: selectedTemplate.name || 'WhatsApp Template',
      content: selectedTemplate.body || '',
      recipients,
      template_id: selectedTemplate.id,
      template_name: selectedTemplate.name,
      template_language: selectedTemplate.language,
      template: templateToSend,
      media_url: mediaUrl || undefined,
    };
  };

  const openSendConfirm = async () => {
    if (!selectedTemplate) return;
    if (selectedHeaderFormat && !mediaUrl) {
      setToast({
        open: true,
        message: `Upload ${selectedHeaderFormat.toLowerCase()} file for this template header before sending.`,
        severity: 'error',
      });
      return;
    }
    setSendConfirmOpen(true);
    setRecipientPreviewCount(null);
    setPreviewCountError('');
    setPreviewCountLoading(true);
    try {
      const res = await CustomAxios.post('oasis/campaign_message/', {
        ...buildWhatsAppPayload(),
        preview_only: true,
      });
      const n = res?.data?.recipient_count;
      setRecipientPreviewCount(typeof n === 'number' ? n : parseInt(String(n), 10) || 0);
    } catch (e) {
      setPreviewCountError(
        e?.response?.data?.message || e?.response?.data?.error || e.message || 'Could not load recipient count.'
      );
      setRecipientPreviewCount(null);
    } finally {
      setPreviewCountLoading(false);
    }
  };

  const closeSendConfirm = (event, reason) => {
    if (reason === 'backdropClick') return;
    if (sending) return;
    setSendConfirmOpen(false);
    setRecipientPreviewCount(null);
    setPreviewCountError('');
    setPreviewCountLoading(false);
  };

  const handleSend = async () => {
    if (!selectedTemplate) return;
    if (selectedHeaderFormat && !mediaUrl) {
      setToast({
        open: true,
        message: `Upload ${selectedHeaderFormat.toLowerCase()} file for this template header before sending.`,
        severity: 'error',
      });
      return;
    }
    setSending(true);
    try {
      const payload = buildWhatsAppPayload();
      const res = await CustomAxios.post('oasis/campaign_message/', payload);
      const id = res?.data?.campaign_id || res?.data?.campaignId || res?.data?.id;
      setCampaignId(id);
      setProgress(res?.data || null);
      fetchCampaignHistory();
      setToast({ open: true, message: 'WhatsApp campaign started.', severity: 'success' });
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data?.error || 'Failed to start WhatsApp campaign.';
      setToast({ open: true, message: msg, severity: 'error' });
    } finally {
      setSending(false);
      setSendConfirmOpen(false);
      setRecipientPreviewCount(null);
      setPreviewCountError('');
      setPreviewCountLoading(false);
    }
  };

  const handleUploadTemplateMedia = async () => {
    if (!mediaFile || !selectedHeaderFormat) return;
    setMediaUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', mediaFile);
      formData.append('media_type', selectedHeaderFormat.toLowerCase());
      const res = await CustomAxios.post('oasis/whatsapp/campaign-media-upload/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const uploadedUrl = res?.data?.media_url || '';
      if (!uploadedUrl) {
        throw new Error('Upload succeeded but media_url not returned.');
      }
      setMediaUrl(uploadedUrl);
      setToast({ open: true, message: 'Template media uploaded.', severity: 'success' });
    } catch (e) {
      const msg = e?.response?.data?.error || e.message || 'Failed to upload template media.';
      setToast({ open: true, message: msg, severity: 'error' });
    } finally {
      setMediaUploading(false);
    }
  };

  return (
    <Box sx={{ width: '100%', marginTop: '25px', padding: '27px', backgroundColor: '#F8FAFC' }}>
      <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 2, flexDirection: { xs: 'column', lg: 'row' } }}>
        <Box sx={{ flex: { xs: '1 1 100%', lg: '0 0 70%' }, maxWidth: { xs: '100%', lg: '70%' }, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Box sx={{ mb: 0.5 }}>
          <Typography sx={{ color: 'var(--Pallate-green-500, #288d2c)', fontFamily: 'inter', fontSize: '24px', fontWeight: 500 }}>
            Communication (WhatsApp)
          </Typography>
          {campaignId ? (
            <Box sx={{ mt: 1 }}>
              <Typography sx={{ color: '#1E293B', fontFamily: 'inter', mb: 0.5 }}>
                Campaign {campaignId}
              </Typography>
              <Typography sx={{ color: '#1E293B', fontFamily: 'inter' }}>
                Queued: {progress?.queued || 0} | Sent: {progress?.sent_count || 0} | Failed: {progress?.failed_count || 0} | Pending: {progress?.pending_count || 0}
              </Typography>
              {progress?.status ? (
                <Chip
                  label={`Status: ${progress.status}`}
                  size="small"
                  sx={{
                    mt: 1,
                    width: 'fit-content',
                    fontFamily: 'inter',
                    bgcolor: progress.status === 'completed' ? '#ECFDF3' : '#F1F5F9',
                    color: progress.status === 'completed' ? '#047857' : '#334155',
                    border: '1px solid #CBD5E1',
                  }}
                />
              ) : null}
            </Box>
          ) : null}
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.2,
            p: 2,
            border: '1px solid #CBD5E1',
            borderRadius: 1,
            background: '#F8FAFC',
          }}
        >
          <Typography sx={{ fontFamily: 'inter', fontSize: '16px', fontWeight: 600, color: '#1E293B' }}>
            Recipient
          </Typography>
          {recipientFilterRows.length === 0 ? (
            <Typography sx={{ fontFamily: 'inter', color: '#64748B', fontSize: '14px' }}>
              No filters found. Use Back to apply filters.
            </Typography>
          ) : (
            recipientFilterRows.map((row) => (
              <Box key={row.id} sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography sx={{ fontFamily: 'inter', color: '#64748B', fontSize: '14px', minWidth: 180 }}>
                  {row.label}
                </Typography>
                <Typography sx={{ fontFamily: 'inter', color: '#1E293B', fontSize: '14px', fontWeight: 500 }}>
                  {row.value}
                </Typography>
              </Box>
            ))
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <FormControl size="small" sx={{ width: '100%' }}>
            <InputLabel id="whatsapp-template-select-label">Template</InputLabel>
            <Select
              labelId="whatsapp-template-select-label"
              value={selectedTemplateId}
              label="Template"
              onChange={(e) => setSelectedTemplateId(String(e.target.value))}
              disabled={loadingTemplates || templates.length === 0}
              sx={{backgroundColor: '#FFF'}}
            >
              {templates.map((tpl) => (
                <MenuItem key={tpl.id} value={String(tpl.id)}>
                  {tpl.name} ({tpl.status || '-'})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            onClick={fetchTemplates}
            disabled={loadingTemplates}
            startIcon={<RefreshIcon />}
            sx={{ borderColor: '#CBD5E1', color: '#1E293B', textTransform: 'none',backgroundColor: '#FFF' }}
          >
            Refresh
          </Button>
        </Box>

        {templateError ? (
          <Typography sx={{ fontFamily: 'inter', color: '#DC2626', fontSize: '13px' }}>{templateError}</Typography>
        ) : null}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 2, border: '1px solid #CBD5E1', borderRadius: 1,backgroundColor: '#FFF' }}>
          <Typography sx={{ fontFamily: 'inter', fontSize: '16px', color: '#1E293B', fontWeight: 500 }}>
            Template content
          </Typography>
          {!selectedTemplate ? (
            <Typography sx={{ fontFamily: 'inter', color: '#64748B', fontSize: '13px' }}>
              Select a template to preview message.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1, background: '#E5DDD5', borderRadius: 1 }}>
              <Box
                sx={{
                  maxWidth: '85%',
                  background: '#DCF8C6',
                  borderRadius: '10px 10px 2px 10px',
                  p: 1.25,
                    boxShadow: '0 1px 1px rgba(0,0,0,0.1)',
                  marginTop: '50px',
                }}
              >
                {previewHeaderImage ? (
                  <Box
                    component="img"
                    src={previewHeaderImage}
                    alt="Template header"
                    sx={{
                      width: '100%',
                      maxHeight: 220,
                      objectFit: 'cover',
                      borderRadius: 1,
                      mb: 1,
                      border: '1px solid #c5e7b5',
                      background: '#f1f5f9',
                    }}
                  />
                ) : null}
                {previewHeaderText ? (
                  <Typography
                    sx={{
                      fontFamily: 'inter',
                      color: '#111B21',
                      fontSize: '13px',
                      fontWeight: 600,
                      whiteSpace: 'pre-wrap',
                      mb: 0.8,
                    }}
                  >
                    {previewHeaderText}
                  </Typography>
                ) : null}
                <Typography sx={{ fontFamily: 'inter', color: '#111B21', fontSize: '13px', whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>
                  {previewBodyText}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        {selectedHeaderFormat ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 2, border: '1px solid #CBD5E1', borderRadius: 1, backgroundColor: '#FFF' }}>
            <Typography sx={{ fontFamily: 'inter', fontSize: '15px', color: '#1E293B', fontWeight: 500 }}>
              Upload header {selectedHeaderFormat.toLowerCase()}
            </Typography>
            <Typography sx={{ fontFamily: 'inter', color: '#64748B', fontSize: '13px' }}>
              This template requires a {selectedHeaderFormat.toLowerCase()} header. Upload file to store under campaign media and use it at send time.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.2, flexWrap: 'wrap' }}>
              <Box
                onClick={() => {
                  setMediaSource('device');
                  setMediaFile(null);
                  setMediaUrl('');
                }}
                sx={{
                  flex: '1 1 220px',
                  minWidth: 220,
                  p: 1.5,
                  borderRadius: 1.5,
                  border: mediaSource === 'device' ? '1px solid #288d2c' : '1px solid #CBD5E1',
                  backgroundColor: mediaSource === 'device' ? '#F0FDF4' : '#FFF',
                  cursor: 'pointer',
                }}
              >
                <Typography sx={{ fontFamily: 'inter', fontSize: '14px', fontWeight: 600, color: '#1E293B' }}>
                  Upload from device
                </Typography>
                <Typography sx={{ fontFamily: 'inter', fontSize: '12px', color: '#64748B', mt: 0.5 }}>
                  Choose a new {selectedHeaderFormat.toLowerCase()} file and upload it for this campaign.
                </Typography>
              </Box>
              <Box
                onClick={() => {
                  setMediaSource('cloud');
                  setMediaFile(null);
                  setMediaUrl('');
                }}
                sx={{
                  flex: '1 1 220px',
                  minWidth: 220,
                  p: 1.5,
                  borderRadius: 1.5,
                  border: mediaSource === 'cloud' ? '1px solid #288d2c' : '1px solid #CBD5E1',
                  backgroundColor: mediaSource === 'cloud' ? '#F0FDF4' : '#FFF',
                  cursor: 'pointer',
                }}
              >
                <Typography sx={{ fontFamily: 'inter', fontSize: '14px', fontWeight: 600, color: '#1E293B' }}>
                  Choose from cloud
                </Typography>
                <Typography sx={{ fontFamily: 'inter', fontSize: '12px', color: '#64748B', mt: 0.5 }}>
                  Reuse existing campaign media already uploaded to storage.
                </Typography>
              </Box>
            </Box>
            {mediaSource === 'device' ? (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    p: 1.5,
                    borderRadius: 1.5,
                    border: '1px dashed #CBD5E1',
                    backgroundColor: '#F8FAFC',
                  }}
                >
                  <Typography sx={{ fontFamily: 'inter', color: '#1E293B', fontSize: '13px' }}>
                    {mediaFile ? mediaFile.name : `No ${selectedHeaderFormat.toLowerCase()} selected`}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1.2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Button
                      component="label"
                      variant="outlined"
                      sx={{ textTransform: 'none', minHeight: 40 }}
                    >
                      Choose file
                      <input
                        hidden
                        type="file"
                        accept={mediaAccept}
                        onChange={(e) => {
                          const file = e.target.files?.[0] || null;
                          setMediaFile(file);
                          setMediaUrl('');
                        }}
                      />
                    </Button>
                    {mediaFile ? (
                      <Button
                        variant="contained"
                        onClick={handleUploadTemplateMedia}
                        disabled={!mediaFile || mediaUploading}
                        sx={{
                          minHeight: 40,
                          background: '#288d2c',
                          '&:hover': { background: '#237a26' },
                          textTransform: 'none',
                          lineHeight: '16px',
                        }}
                      >
                        {mediaUploading ? 'Uploading...' : 'Upload media'}
                      </Button>
                    ) : null}
                    {mediaFile ? (
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setMediaFile(null);
                          setMediaUrl('');
                        }}
                        disabled={mediaUploading}
                        sx={{
                          minHeight: 40,
                          borderColor: '#CBD5E1',
                          color: '#1E293B',
                          textTransform: 'none',
                        }}
                      >
                        Remove file
                      </Button>
                    ) : null}
                  </Box>
                </Box>
              </>
            ) : (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    p: 1.5,
                    borderRadius: 1.5,
                    border: '1px solid #E2E8F0',
                    backgroundColor: '#F8FAFC',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontFamily: 'inter', color: '#1E293B', fontSize: '13px', fontWeight: 600 }}>
                      Campaign media
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={async () => {
                        setLoadingCloudMedia(true);
                        try {
                          const res = await CustomAxios.get(
                            `oasis/whatsapp/campaign-media/?media_type=${selectedHeaderFormat.toLowerCase()}`
                          );
                          setCloudMedia(res?.data?.data || []);
                        } catch (e) {
                          const msg = e?.response?.data?.error || e.message || 'Failed to load cloud media.';
                          setToast({ open: true, message: msg, severity: 'error' });
                        } finally {
                          setLoadingCloudMedia(false);
                        }
                      }}
                      sx={{ textTransform: 'none' }}
                    >
                      Refresh
                    </Button>
                  </Box>
                  <Box
                    sx={{
                      maxHeight: 240,
                      overflowY: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                    }}
                  >
                    {(cloudMedia || []).map((item) => {
                      const selected = mediaUrl === item.media_url;
                      return (
                        <Box
                          key={item.media_url}
                          onClick={() => {
                            setMediaUrl(String(item.media_url));
                            setMediaFile(null);
                          }}
                          sx={{
                            p: 1.25,
                            borderRadius: 1,
                            border: selected ? '1px solid #288d2c' : '1px solid #CBD5E1',
                            backgroundColor: selected ? '#F0FDF4' : '#FFF',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1.5,
                          }}
                        >
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography sx={{ fontFamily: 'inter', color: '#1E293B', fontSize: '13px', fontWeight: 500 }}>
                              {item.name}
                            </Typography>
                            <Typography sx={{ fontFamily: 'inter', color: '#64748B', fontSize: '12px', mt: 0.4 }}>
                              {item.content_type || selectedHeaderFormat.toLowerCase()}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              width: 88,
                              height: "90%",
                              borderRadius: 1,
                              overflow: 'hidden',
                              border: '1px solid #CBD5E1',
                              backgroundColor: '#F8FAFC',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            {String(item.content_type || '').startsWith('image/') ? (
                              <Box
                                component="img"
                                src={item.media_url}
                                alt={item.name}
                                sx={{ width: '50%', height: '50%', objectFit: 'cover' }}
                              />
                            ) : String(item.content_type || '').startsWith('video/') ? (
                              <Box
                                component="video"
                                src={item.media_url}
                                muted
                                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            ) : (
                              <Typography sx={{ fontFamily: 'inter', color: '#64748B', fontSize: '11px', textAlign: 'center', px: 1 }}>
                                {selectedHeaderFormat}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
                {loadingCloudMedia ? <LinearProgress sx={{ mt: 0.5 }} /> : null}
                {!loadingCloudMedia && cloudMedia.length === 0 ? (
                  <Typography sx={{ fontFamily: 'inter', color: '#64748B', fontSize: '13px' }}>
                    No campaign media found in cloud for this format.
                  </Typography>
                ) : null}
              </>
            )}
            {mediaUploading ? <LinearProgress sx={{ mt: 0.5 }} /> : null}
            {mediaUrl ? (
              <Typography sx={{ fontFamily: 'inter', color: '#166534', fontSize: '12px', wordBreak: 'break-all' }}>
                {mediaSource === 'cloud' ? 'Cloud media selected.' : 'Uploaded media ready.'}
              </Typography>
            ) : null}
          </Box>
        ) : null}

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="contained"
            onClick={openSendConfirm}
            disabled={!canSendCampaign}
            startIcon={<SendIcon />}
            sx={{ background: '#288d2c', '&:hover': { background: '#237a26' }, textTransform: 'none' }}
          >
            Send
          </Button>
          <Button
            variant="outlined"
            startIcon={<ClearAllIcon />}
            onClick={() => navigate('/communication/whatsapp/bulk')}
            sx={{ borderColor: '#288d2c', color: '#288d2c', '&:hover': { borderColor: '#288d2c' } }}
          >
            Back
          </Button>
        </Box>
      </Box>

        <Box
          sx={{
            flex: { xs: '1 1 100%', lg: '0 0 30%' },
            maxWidth: { xs: '100%', lg: '30%' },
            p: 1.5,
            border: '1px solid #CBD5E1',
            borderRadius: 1,
            backgroundColor: '#FFF',
            height: { xs: 'auto', lg: 'calc(100vh - 120px)' },
            display: 'flex',
            flexDirection: 'column',
            position: { xs: 'static', lg: 'sticky' },
            top: { xs: 'auto', lg: 20 },
          }}
        >
          <Typography sx={{ fontFamily: 'inter', fontSize: '14px', fontWeight: 600, color: '#1E293B', mb: 1 }}>
            WhatsApp Campaign History
          </Typography>
          {loadingHistory ? (
            <Typography sx={{ fontFamily: 'inter', fontSize: '12px', color: '#64748B' }}>Loading…</Typography>
          ) : campaignHistory.length === 0 ? (
            <Typography sx={{ fontFamily: 'inter', fontSize: '12px', color: '#64748B' }}>No history yet.</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, overflowY: 'auto', flex: 1 }}>
              {campaignHistory.map((item) => (
                <Accordion key={item.campaign_id} disableGutters elevation={0} sx={{ border: '1px solid #E2E8F0' }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontFamily: 'inter', fontSize: '12px', color: '#1E293B', fontWeight: 600 }}>
                        #{item.campaign_id} - {item.status || 'processing'}
                      </Typography>
                      <Typography sx={{ fontFamily: 'inter', fontSize: '11px', color: '#64748B' }}>
                        Total: {item.total_recipients || 0} | Sent: {item.sent_count || 0} | Delivered: {item.delivered_count || 0} | Read: {item.read_count || 0}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <Typography sx={{ fontFamily: 'inter', fontSize: '11px', color: '#1E293B', whiteSpace: 'pre-wrap' }}>
                      <strong>Details:</strong> {formatFilters(item)}
                    </Typography>
                    <Typography sx={{ fontFamily: 'inter', fontSize: '11px', color: '#1E293B', mb: 0.5 }}>
                      <strong>Template:</strong> {item.subject || '-'}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </Box>
      </Box>

      <Dialog open={sendConfirmOpen} onClose={closeSendConfirm} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: 'inter', fontWeight: 600 }}>Confirm send</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: 'inter', fontSize: '14px', fontWeight: 600, color: '#1E293B', mb: 1 }}>
            Recipient criteria
          </Typography>
          <Box
            component="ul"
            sx={{
              m: 0,
              mb: 2,
              pl: 2.5,
              fontFamily: 'inter',
              fontSize: '14px',
              color: '#334155',
              '& li': { mb: 0.75 },
            }}
          >
            {recipientFilterRows.map((row) => (
              <Box component="li" key={row.id}>
                <strong>{row.label}:</strong> {row.value}
              </Box>
            ))}
          </Box>
          <Typography sx={{ fontFamily: 'inter', fontSize: '14px', fontWeight: 600, color: '#1E293B', mb: 0.5 }}>
            Matching recipients
          </Typography>
          {previewCountLoading ? (
            <Typography sx={{ fontFamily: 'inter', fontSize: '14px', color: '#64748B' }}>Calculating…</Typography>
          ) : previewCountError ? (
            <Typography sx={{ fontFamily: 'inter', fontSize: '14px', color: '#B91C1C' }}>{previewCountError}</Typography>
          ) : (
            <DialogContentText sx={{ fontFamily: 'inter', fontSize: '15px', color: '#1E293B', m: 0 }}>
              <strong>{recipientPreviewCount}</strong> member{recipientPreviewCount === 1 ? '' : 's'} with a WhatsApp
              number will be queued for this campaign.
              {recipientPreviewCount === 0 ? (
                <Typography component="span" sx={{ display: 'block', mt: 1, color: '#B91C1C', fontSize: '14px' }}>
                  No recipients with a WhatsApp number found. Adjust filters before sending.
                </Typography>
              ) : null}
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeSendConfirm} disabled={sending} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={() => handleSend()}
            variant="contained"
            disabled={
              sending ||
              previewCountLoading ||
              previewCountError ||
              recipientPreviewCount == null ||
              recipientPreviewCount === 0
            }
            sx={{ background: '#288d2c', '&:hover': { background: '#237a26' }, textTransform: 'none' }}
          >
            {sending ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Confirm send'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setToast((t) => ({ ...t, open: false }))} severity={toast.severity} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default CommunicationWhatsApp;
